/**
 * The core's state-in / state-out API: `step`, `cloneState`, `serializeState`,
 * `deserializeState`.
 *
 * ===========================================================================
 * ALIASING — READ THIS BEFORE HOLDING A REFERENCE ACROSS A STEP
 * ===========================================================================
 *
 * `step(state, inputs)` MUTATES `state` AND RETURNS THE SAME OBJECT.
 *
 *     const next = step(state, inputs);
 *     next === state;                       // always true
 *     // `state` has changed. So has every car, the ball, and every Vec3 you
 *     // pulled out of them. Anything you kept a reference to is now the NEW
 *     // value, not the old one.
 *
 * A caller that needs the previous tick must copy what it needs before
 * stepping, or call `cloneState` — the renderer's interpolation buffers in
 * src/main.ts are exactly this, and they copy into fixed scratch objects rather
 * than cloning.
 *
 * WHY. A world is roughly 2 kB of live objects — six cars with a Float64Array
 * orientation each, thirty-four pads, a ball — and allocating a fresh one every
 * tick puts a full copy plus the garbage it becomes on the per-tick path. At the
 * 20 us/tick this core targets, that is not a rounding error: an early
 * allocate-per-tick prototype of this API ran at roughly half the mutating one
 * and spent the difference in GC. Immutability would be nicer to reason about
 * and it is not affordable here, so the aliasing is documented loudly instead of
 * being discovered.
 *
 * What is guaranteed instead:
 *
 *   - `cloneState(state)` is a genuine DEEP copy. The result shares nothing
 *     mutable with the original — not a Vec3, not an orientation matrix, not a
 *     pad. It does share the ARENA, which is immutable static geometry and is
 *     the one thing worth sharing (its BVH costs milliseconds to build).
 *   - `serializeState` / `deserializeState` round-trip through plain JSON with
 *     no loss. `test/sim/determinism.test.ts` proves it by stepping 1000 ticks
 *     either side of a round trip and comparing.
 *
 * ===========================================================================
 * WHAT IS AND IS NOT STATE
 * ===========================================================================
 *
 * State is everything that changes the next tick: the tick counter, the RNG, the
 * match, the ball, every car (including the powerslide ramp, the boost burst and
 * the jump-button edge, which used to live in module WeakMaps and so were
 * invisible to any copy), the boost pads, the per-car psyonix cooldown and the
 * last-touch record.
 *
 * A car's BODY is state too, and travels as a NAME rather than as its numbers:
 * `body: 'dominus'` re-looked-up through `carConfigFor` on the other side. The
 * alternative — writing the hitbox and the wheel table into every payload —
 * would let a stored state disagree with ../physics/car-config about what a
 * Dominus is, which is the same argument that keeps `ResolvedPhysics` out of the
 * payload. Six cars' worth of geometry per tick of a training rollout is also
 * not free.
 *
 * Not state: the arena, the match CONFIG and the MUTATOR config (both constant
 * for a match, but carried anyway so a deserialized world does not need them
 * supplied), and the contact scratch buffers, which are dead between ticks.
 *
 * The mutators are carried rather than derived because they are not derivable:
 * a world restored without them would silently play the default game, and a
 * worker handed a state would run different physics from the thread that sent
 * it. `ResolvedPhysics` is NOT serialized — it is rebuilt from the config on
 * the other side, so there is exactly one place the mapping lives
 * (../physics/mutators) and no way for a stored payload to disagree with it.
 */

import type { GoalEvent, MatchConfig, MatchSnapshot } from '../game/match';
import { carConfigFor, type CarBody } from '../physics/car-config';
import { makeCar, type CarInput, type CarState } from '../physics/car-state';
import { DT } from '../physics/constants';
import { normalizeMutators, type MutatorConfig } from '../physics/mutators';
import type { ArenaGeometry } from '../physics/surface';
import { World, type WorldStepEvents } from './world';

/** The simulation state. It is a `World`; the alias names the role. */
export type SimState = World;

// ---------------------------------------------------------------------------
// step
// ---------------------------------------------------------------------------

/**
 * Advance the simulation by one tick and return it.
 *
 * THE RETURNED STATE IS THE ARGUMENT, mutated. See the aliasing section at the
 * top of this file. Use `stepEvents` instead when you also want the tick's
 * event record.
 */
export const step = (state: SimState, inputs: readonly CarInput[], dt: number = DT): SimState => {
  state.step(inputs, dt);
  return state;
};

/** `step`, also returning what happened. Same aliasing rules. */
export const stepEvents = (
  state: SimState,
  inputs: readonly CarInput[],
  dt: number = DT
): WorldStepEvents => state.step(inputs, dt);

// ---------------------------------------------------------------------------
// The serialized form
// ---------------------------------------------------------------------------

/**
 * Bumped whenever the shape below changes incompatibly.
 *
 * v2 added `mutators` and the per-car `boostRechargeTimer`. A v1 payload has no
 * mutator config at all, and defaulting it would be a guess about which rules a
 * stored match was played under, so v1 is rejected rather than migrated.
 */
export const STATE_FORMAT_VERSION = 2;

interface SerializedVec3 {
  x: number;
  y: number;
  z: number;
}

export interface SerializedCar {
  team: 0 | 1;
  /**
   * The car's body (../physics/car-config). OPTIONAL, and absent means Octane —
   * which is not a default so much as a fact about the payload: before bodies
   * existed every car in this simulation was an Octane, so a v2 payload written
   * without this field describes a field of Octanes exactly. That is why it does
   * not need the version bump a genuinely ambiguous field would.
   */
  body?: CarBody;
  position: SerializedVec3;
  velocity: SerializedVec3;
  angularVelocity: SerializedVec3;
  /** Row-major, nine entries. */
  orientation: number[];
  surface: CarState['surface'];
  surfaceNormal: SerializedVec3;
  onGround: boolean;
  boostAmount: number;
  supersonic: boolean;
  supersonicTimer: number;
  hasJumped: boolean;
  hasFlip: boolean;
  isDodging: boolean;
  jumpHoldTime: number;
  airTime: number;
  dodgeTime: number;
  dodgeDirection: SerializedVec3;
  dodgeCooldown: number;
  prevJumpDown: boolean;
  powerslide: number;
  boostBurst: { active: boolean; time: number };
  /** Seconds since this car last spent boost; only ever nonzero under the
   *  RECHARGE boost mutator. */
  boostRechargeTimer: number;
  demolished: boolean;
  respawnTimer: number;
  /**
   * Who this car last bumped, and how long until it may bump them again
   * (../physics/car-state `carContact`). Carried because the cooldown is per
   * ATTACKER and keyed to one VICTIM: dropping it would let a restored world
   * re-bump a car it had just bumped, and would make a snapshot/restore diverge
   * from the world it was taken from within a quarter of a second of any
   * contact.
   *
   * OPTIONAL for the same reason `body` is: a v2 payload written before bumps
   * existed describes a field on which no car had ever bumped anything, and
   * `{ -1, 0 }` is exactly that field rather than a guess about it.
   */
  carContact?: { otherCarIndex: number; cooldownTimer: number };
  /**
   * Tick of this car's last psyonix impulse, or null for "never". JSON has no
   * -Infinity, and writing 0 instead would let a car fire its extra impulse a
   * tick early after a restore.
   */
  lastImpulseTick: number | null;
}

export interface SerializedState {
  version: number;
  tick: number;
  /** The PRNG's whole state (../physics/rng). */
  rng: number;
  matchConfig: MatchConfig;
  /** The match's mutators. Rebuilt into a `ResolvedPhysics` on restore. */
  mutators: MutatorConfig;
  match: MatchSnapshot;
  ball: {
    position: SerializedVec3;
    velocity: SerializedVec3;
    angularVelocity: SerializedVec3;
  };
  cars: SerializedCar[];
  pads: { active: boolean; respawnTimer: number }[];
  lastTouch: { carIndex: number; team: 0 | 1; tick: number } | null;
}

const v3 = (v: SerializedVec3): SerializedVec3 => ({ x: v.x, y: v.y, z: v.z });

const serializeCar = (car: CarState, lastImpulseTick: number): SerializedCar => ({
  team: car.team,
  body: car.config.body,
  position: v3(car.position),
  velocity: v3(car.velocity),
  angularVelocity: v3(car.angularVelocity),
  orientation: Array.from(car.orientation),
  surface: car.surface,
  surfaceNormal: v3(car.surfaceNormal),
  onGround: car.onGround,
  boostAmount: car.boostAmount,
  supersonic: car.supersonic,
  supersonicTimer: car.supersonicTimer,
  hasJumped: car.hasJumped,
  hasFlip: car.hasFlip,
  isDodging: car.isDodging,
  jumpHoldTime: car.jumpHoldTime,
  airTime: car.airTime,
  dodgeTime: car.dodgeTime,
  dodgeDirection: v3(car.dodgeDirection),
  dodgeCooldown: car.dodgeCooldown,
  prevJumpDown: car.prevJumpDown,
  powerslide: car.powerslide,
  boostBurst: { active: car.boostBurst.active, time: car.boostBurst.time },
  boostRechargeTimer: car.boostRechargeTimer,
  demolished: car.demolished,
  respawnTimer: car.respawnTimer,
  carContact: {
    otherCarIndex: car.carContact.otherCarIndex,
    cooldownTimer: car.carContact.cooldownTimer,
  },
  lastImpulseTick: Number.isFinite(lastImpulseTick) ? lastImpulseTick : null,
});

const restoreCar = (car: CarState, s: SerializedCar): void => {
  car.config = carConfigFor(s.body ?? 'octane');
  car.position = v3(s.position);
  car.velocity = v3(s.velocity);
  car.angularVelocity = v3(s.angularVelocity);
  car.orientation = Float64Array.from(s.orientation);
  car.surface = s.surface;
  car.surfaceNormal = v3(s.surfaceNormal);
  car.onGround = s.onGround;
  car.boostAmount = s.boostAmount;
  car.supersonic = s.supersonic;
  car.supersonicTimer = s.supersonicTimer;
  car.hasJumped = s.hasJumped;
  car.hasFlip = s.hasFlip;
  car.isDodging = s.isDodging;
  car.jumpHoldTime = s.jumpHoldTime;
  car.airTime = s.airTime;
  car.dodgeTime = s.dodgeTime;
  car.dodgeDirection = v3(s.dodgeDirection);
  car.dodgeCooldown = s.dodgeCooldown;
  car.prevJumpDown = s.prevJumpDown;
  car.powerslide = s.powerslide;
  car.boostBurst = { active: s.boostBurst.active, time: s.boostBurst.time };
  car.boostRechargeTimer = s.boostRechargeTimer;
  car.demolished = s.demolished;
  car.respawnTimer = s.respawnTimer;
  car.carContact = {
    otherCarIndex: s.carContact?.otherCarIndex ?? -1,
    cooldownTimer: s.carContact?.cooldownTimer ?? 0,
  };
  car.team = s.team;
};

/**
 * The whole simulation as plain JSON-safe data. Deep: nothing in the result
 * aliases anything in `state`.
 */
export const serializeState = (state: SimState): SerializedState => ({
  version: STATE_FORMAT_VERSION,
  tick: state.tick,
  rng: state.rng.state,
  matchConfig: { ...state.match.config },
  mutators: { ...state.mutators },
  match: state.match.snapshot(),
  ball: {
    position: v3(state.ball.position),
    velocity: v3(state.ball.velocity),
    angularVelocity: v3(state.ball.angularVelocity),
  },
  cars: state.cars.map((car, i) => serializeCar(car, state.psyonixCooldownTick(i))),
  pads: state.boostPads.pads.map((p) => ({ active: p.active, respawnTimer: p.respawnTimer })),
  lastTouch: state.lastTouch ? { ...state.lastTouch } : null,
});

export interface DeserializeOptions {
  /**
   * The arena to attach. Geometry is not serialized — it is many megabytes of
   * static triangles that every world in a training batch shares — so a
   * restored world takes the memoized soccar arena unless told otherwise.
   */
  arena?: ArenaGeometry;
}

/** Inverse of `serializeState`. Builds a fresh world; nothing is shared with
 *  `data` except by value. */
export const deserializeState = (
  data: SerializedState,
  options: DeserializeOptions = {}
): SimState => {
  if (data.version !== STATE_FORMAT_VERSION) {
    throw new Error(
      `state format version ${data.version} != ${STATE_FORMAT_VERSION}; no migration exists`
    );
  }

  // The roster is rebuilt from the serialized teams rather than from a team
  // size, so a non-symmetric match round-trips.
  const world = new World({
    arena: options.arena,
    cars: data.cars.map((c) => makeCar(c.team, carConfigFor(c.body ?? 'octane'))),
    matchConfig: { ...data.matchConfig },
    // Total, so a hand-written or truncated payload cannot turn a multiplier
    // into `undefined` and every derived number into NaN.
    mutators: normalizeMutators(data.mutators),
    seed: data.rng,
  });
  applySerialized(world, data);
  return world;
};

/**
 * Overwrite `world` with `data`. Split out of `deserializeState` because
 * `cloneState` needs exactly this and building a second world to throw away
 * would double the cost of a clone.
 */
const applySerialized = (world: World, data: SerializedState): void => {
  world.tick = data.tick;
  world.rng.state = data.rng;
  world.match.restore(data.match);

  world.ball.position = v3(data.ball.position);
  world.ball.velocity = v3(data.ball.velocity);
  world.ball.angularVelocity = v3(data.ball.angularVelocity);

  for (let i = 0; i < data.cars.length; i++) {
    restoreCar(world.cars[i], data.cars[i]);
    world.setPsyonixCooldownTick(
      i,
      data.cars[i].lastImpulseTick ?? Number.NEGATIVE_INFINITY
    );
  }

  for (let i = 0; i < data.pads.length; i++) {
    world.boostPads.pads[i].active = data.pads[i].active;
    world.boostPads.pads[i].respawnTimer = data.pads[i].respawnTimer;
  }

  world.lastTouch = data.lastTouch ? { ...data.lastTouch } : null;
};

// ---------------------------------------------------------------------------
// clone
// ---------------------------------------------------------------------------

/**
 * A deep copy. The result shares no mutable object with `state`; it does share
 * the ARENA, which is immutable and expensive to build.
 *
 * Implemented through the serialized form rather than by hand so that the two
 * cannot drift: a field that `serializeState` forgets is a field `cloneState`
 * forgets, and the determinism suite exercises both at once.
 */
export const cloneState = (state: SimState): SimState =>
  deserializeState(serializeState(state), { arena: state.arena });

/** JSON text, for a file or a worker message. */
export const stateToJSON = (state: SimState): string => JSON.stringify(serializeState(state));

export const stateFromJSON = (json: string, options: DeserializeOptions = {}): SimState =>
  deserializeState(JSON.parse(json) as SerializedState, options);

/** Re-exported so a caller only needs this module. */
export type { GoalEvent, WorldStepEvents };
